// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import * as React from 'react';

import { type Route, type NavigateOptions, type BeforeNavigationArgs } from './types';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'UnnamedComponent';
}

/** Router context type */
export type RouteContext = {|
  /** The current route */
  +route: Route,
  /** The previous route */
  +previousRoute: ?{ [string]: string },
  /** A function that takes a request to navigate to another route */
  +navigate: (Route, options?: NavigateOptions) => any,
  /**
   * Add a predicate that will be called before SPA navigation occurs. It can return true to stop
   * navigation until continueNavigation() is called
   */
  +addBeforeNavigationCallback: ((BeforeNavigationArgs) => boolean) => any,
  /**
   * Remove a callback added with addBeforeNavigationCallback()
   */
  +removeBeforeNavigationCallback: ((BeforeNavigationArgs) => boolean) => any,
  /** If true, navigate back to a previous route */
  +popping: boolean,
  +[string]: any,
|};

/**
 * Context for routing in the TA app.
 */
const routerContext: React.Context<RouteContext> = React.createContext({
  // TODO: use Flow types
  /** The current route */
  route: {},

  /** The previous route */
  previousRoute: {},

  /**
   * A function that takes a request to navigate to another route and handles
   * it however it would like.
   */
  navigate: () => undefined,

  addBeforeNavigationCallback: () => {},

  removeBeforeNavigationCallback: () => {},

  /**
   * Whether we are moving back to a previous route.
   *
   * @deprecated this will be removed in the future
   */
  popping: false,
});

/** Get Provider and Consumer from routerContext */
const { Provider, Consumer } = routerContext;

/** Function that adds the context to components */
export function withRouteContext(Component: React.ComponentType<*>) {
  const result = (props: any) => (
    <Consumer>
      {({ route, navigate, previousRoute, popping }) => (
        <Component
          popping={popping}
          route={route}
          previousRoute={previousRoute}
          navigate={navigate}
          props={props}
        />
      )}
    </Consumer>
  );
  result.displayName = `withRouteContext(${getDisplayName(Component)})`;
  return result;
}

export default routerContext;
export { Provider as RouteProvider, Consumer as RouteConsumer };
